import React, { useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { ICommunityResource } from '@/types/admin/resources';
import { ResourceCard } from './ResourceCard';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { LoadingSpinner } from '@/components';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';

type ResourceTile = {
  position: number;
  resource?: ICommunityResource;
  isPrimary: boolean;
};

interface ResourceGridProps {
  tiles: ResourceTile[];
  onResourcePress?: (resource: ICommunityResource) => void;
  onEditResource?: (resource: ICommunityResource) => void;
  onEmptySlotPress?: (position: number) => void;
  onCreateResource?: () => void;
  isEditable?: boolean;
  loading?: boolean;
  selectedResourceId?: number | null;
  onSelectResource?: (resourceId: number | null) => void;
  onUpdateResourcePositions?: (resourceId: number, position: number) => Promise<void> | void;
}

export const ResourceGrid: React.FC<ResourceGridProps> = React.memo(({
  tiles,
  onResourcePress,
  onEditResource,
  onEmptySlotPress,
  onCreateResource,
  isEditable = true,
  loading = false,
  selectedResourceId = null,
  onSelectResource,
  onUpdateResourcePositions,
}) => {
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const primaryTiles = useMemo(() => tiles.filter(t => t.position < 4), [tiles]);
  const secondaryTiles = useMemo(() => tiles.filter(t => t.position >= 4), [tiles]);

  const renderEmptySlot = useCallback((position: number) => (
    <TouchableOpacity
      key={`empty-${position}`}
      style={[styles.gridItem, styles.emptySlot]}
      onPress={() => (onEmptySlotPress ? onEmptySlotPress(position) : onCreateResource?.())}
      activeOpacity={0.7}
    >
      <View style={styles.emptySlotContent}>
        <SVG_COMPONENTS.ADD_BUTTON width={24} height={24} color={ADMIN_COLORS.TEXT_TERTIARY} />
        <Text style={styles.emptySlotText}>{selectedResourceId != null ? 'Move here' : 'Add resource'}</Text>
      </View>
    </TouchableOpacity>
  ), [onEmptySlotPress, onCreateResource, selectedResourceId]);

  const renderTile = useCallback((tile: ResourceTile) => {
    if (tile.resource) {
      return (
        <ResourceCard
          key={`res-${tile.resource.id}`}
          resource={tile.resource}
          onPress={async (res) => {
            // If a different resource is selected, move it onto this occupied tile (parity with admin-web)
            if (
              selectedResourceId != null &&
              selectedResourceId !== res.id &&
              typeof onUpdateResourcePositions === 'function'
            ) {
              try {
                await onUpdateResourcePositions(selectedResourceId, res.position);
                showSuccess('Resource moved');
              } catch (err: any) {
                showApiError(err, 'Failed to move resource');
              } finally {
                onSelectResource?.(null);
              }
              return;
            }
            if (onSelectResource) {
              onSelectResource(res.id === selectedResourceId ? null : res.id);
            }
            onResourcePress?.(res);
          }}
          onEdit={onEditResource}
          showEditButton={isEditable}
          isSelected={tile.resource.id === selectedResourceId}
          style={styles.gridItem}
        />
      );
    }
    return renderEmptySlot(tile.position);
  }, [onResourcePress, onEditResource, isEditable, renderEmptySlot, onSelectResource, selectedResourceId, onUpdateResourcePositions, showSuccess, showApiError]);

  const renderPrimaryGrid = () => {
    const gridItems: React.ReactNode[] = [];
    for (let i = 0; i < 4; i++) {
      const tile = primaryTiles.find(t => t.position === i) || { position: i } as ResourceTile;
      gridItems.push(renderTile(tile));
    }

    return (
      <View style={styles.primaryGrid}>
        <View style={styles.gridRow}>
          {gridItems[0]}
          {gridItems[1]}
        </View>
        <View style={styles.gridRow}>
          {gridItems[2]}
          {gridItems[3]}
        </View>
      </View>
    );
  };

  const renderSecondaryGrid = () => {
    if (secondaryTiles.length === 0) {
      return null;
    }
    const rows: React.ReactNode[] = [];
    for (let i = 0; i < secondaryTiles.length; i += 2) {
      const slice = secondaryTiles.slice(i, i + 2);
      rows.push(
        <View key={`row-${i}`} style={styles.gridRow}>
          {slice.map(tile => (
            renderTile(tile)
          ))}
        </View>
      );
    }
    return (
      <View style={styles.secondarySection}>
        <Text style={styles.sectionTitle}>Other Resources</Text>
        {rows}
      </View>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading resources..." />;
  }

  const hasAnyResource = tiles.some(t => Boolean(t.resource));

  return (
    <View style={styles.container}>
      <View style={styles.primarySection}>
        <Text style={styles.sectionTitle}>Residents Dashboard</Text>
        {renderPrimaryGrid()}
      </View>

      {renderSecondaryGrid()}

      {!hasAnyResource && (
        <TouchableOpacity
          style={styles.addFirstResourceButton}
          onPress={onCreateResource}
          activeOpacity={0.7}
        >
          <SVG_COMPONENTS.ADD_BUTTON width={32} height={32} color={ADMIN_COLORS.PRIMARY} />
          <Text style={styles.addFirstResourceText}>Create your first resource</Text>
          <Text style={styles.addFirstResourceSubtext}>
            Resources help residents access important information and services
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
});

ResourceGrid.displayName = 'ResourceGrid';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  primarySection: {
    marginBottom: 32,
  },
  secondarySection: {
    flex: 1,
    gap: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 16,
  },
  primaryGrid: {
    gap: 12,
  },
  gridRow: {
    flexDirection: 'row',
    gap: 12,
  },
  gridItem: {
    flexBasis: '48%',
    maxWidth: '48%',
    minWidth: 0,
    flexGrow: 0,
    height: 160,
  },
  emptySlot: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderRadius: 12,
    borderStyle: 'dashed',
    borderColor: ADMIN_COLORS.BORDER,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptySlotContent: {
    alignItems: 'center',
    gap: 8,
  },
  emptySlotText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    fontWeight: '500',
  },
  addFirstResourceButton: {
    alignItems: 'center',
    padding: 40,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: ADMIN_COLORS.BORDER,
    marginTop: 40,
  },
  addFirstResourceText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginTop: 16,
    marginBottom: 8,
  },
  addFirstResourceSubtext: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
});
